import { SagaIterator } from 'redux-saga'
import { put, takeLatest, select, all, delay } from 'redux-saga/effects'

import { contentStateSelectors } from '@tabeeb/modules/shared/content'
import { signalrActions, signalrConstants, signalrEvents } from '@tabeeb/modules/signalr'
import { getFacingMode, getSelectedUser } from '@tabeeb/modules/presentation/selectors'
import { getCurrentUserId } from '@tabeeb/modules/account/selectors'
import { isMobileOnly } from 'react-device-detect'
import { getContentId } from '@tabeeb/modules/shared/content/selectors'
import { toggleMobileFlash } from '@tabeeb/modules/presentation/actions/tracks'
import { CAMERA_FACING_MODE } from '@tabeeb/modules/presentation/constants'

import * as flashActions from '../actions/flash'
import { FlashResponseDelay } from '../common/flash'
import { getIsFlashInProgress } from '../selectors'

function* onFlashTurnOn({ payload }: ReturnType<typeof flashActions.onFlashTurnOn>): SagaIterator {
  yield put(flashActions.onFlashProgressStateUpdate(true))

  const selectedUser: ReturnType<typeof getSelectedUser> = yield select(getSelectedUser)
  if (!selectedUser) {
    return
  }

  const contentId: ReturnType<typeof contentStateSelectors.getContentId> = yield select(
    contentStateSelectors.getContentId
  )

  yield put(signalrActions.invokeHubAction({ method: 'FlashTurnOn', args: [contentId, selectedUser.id, payload] }))
  yield delay(FlashResponseDelay)

  const flashInProgress: ReturnType<typeof getIsFlashInProgress> = yield select(getIsFlashInProgress)
  if (flashInProgress === true) {
    yield put(flashActions.onFlashProgressStateUpdate(false))
  }
}

function* onFlashUpdate(): SagaIterator {
  yield put(flashActions.onFlashProgressStateUpdate(false))
}

function* onFlashToggleValueUpdated({
  payload: [emitEventUserId, value],
}: {
  payload: [number, boolean]
}): SagaIterator {
  const selectedUser: ReturnType<typeof getSelectedUser> = yield select(getSelectedUser)
  if (selectedUser?.id === emitEventUserId) {
    yield put(flashActions.onFlashUpdate(value))
  }
}

function* onFlashTurnedOn({
  payload: [contentId, userId, enabled],
}: {
  payload: [number, number, boolean]
}): SagaIterator {
  const currentUserId: ReturnType<typeof getCurrentUserId> = yield select(getCurrentUserId)
  const currentContentId: ReturnType<typeof getContentId> = yield select(getContentId)
  const currentFacingMode: ReturnType<typeof getFacingMode> = yield select(getFacingMode)

  if (
    !isMobileOnly ||
    userId !== currentUserId ||
    currentContentId !== contentId ||
    currentFacingMode === CAMERA_FACING_MODE.USER
  ) {
    return
  }

  yield put(toggleMobileFlash(enabled))
  yield put(signalrActions.invokeHubAction({ method: 'UpdateFlashToggleValue', args: [contentId, userId, enabled] }))
}

function* flashSaga() {
  yield all([
    takeLatest(flashActions.onFlashTurnOn, onFlashTurnOn),
    takeLatest(flashActions.onFlashUpdate, onFlashUpdate),
    // signalR
    takeLatest(signalrEvents[signalrConstants.tabeebHubName].onFlashToggleValueUpdated, onFlashToggleValueUpdated),
    takeLatest(signalrEvents[signalrConstants.tabeebHubName].onFlashTurnedOn, onFlashTurnedOn),
  ])
}

export default flashSaga
